import React from "react";

const titleFont = { fontFamily: "Zeitmax, sans-serif" };
const bodyFont = { fontFamily: "Sagewold, sans-serif" };

const COLORS = {
  primary: "#E4570B",
  dark: "#6A310A",
  accent: "#EFA74F",
  sand: "#DFC293",
  green: "#749269",
};

const TEAM_PLACEHOLDER_NAME = "TO BE DETERMINED";
const TEAM_LABEL = "Тим";
// Default placeholder teams (6 teams) kept in this file.
const DEFAULT_TEAMS = [
  { id: 1, name: 'DAS_masters', members: ['Андреј Ристиќ', 'Филип Гавриловски', 'Димитар Арсов'] },
  { id: 2, name: 'Syntax Terminators', members: ['Мила Божиноска', 'Глигор Котески', 'Михајло Крстески', 'Никола Соколоски'] },
  { id: 3, name: 'LeCode', members: ['Никита Јакимовски', 'Бојан Ефтимоски', 'Дарко Трајанов', 'Јаков Спироски', 'Филип Бојаџиевски'] },
  { id: 4, name: 'HTMLadies', members: ['Ема Арсова', 'Теодора Цветковска', 'Ема Филиповска'] },
  { id: 5, name: 'DevDash', members: ['Ермал Баки', 'Станислав Пачуков', 'Јован Стојковски', 'Tеодор Трајчев'] },
  { id: 6, name: 'FutuWATT!?', members: ['Александра Ризоска', 'Иван Јованоски' , 'Борјан Андов', 'Горазд Малиновски']}, 
  { id: 7, name: 'Byte Busters', members: ['Екатерина Ристевска', 'Христијан Гајдов', 'Емилија Костова'] },
  { id: 8, name: 'Creative Minds', members: ['Едис Усеин', 'Драган Јончески', 'Никола Ристовски', 'Aндреј Стојаноски'] }
];

// Split a full name into first part and surname (surname = last token). If only one token, surname is empty.
const splitName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return { first: fullName || '', last: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  const last = parts.pop();
  return { first: parts.join(' '), last };
};
// Members and teams are generated inside the component to allow dynamic counts.
// Each team will have at most 5 members (UI constraint).

function MainContent({ numTeams = 9, membersPerTeam = 5, teams = null }) {
  // Resolve teams: prefer `teams` prop (Option A). Otherwise use DEFAULT_TEAMS
  // (keeps the placeholder data in this file). Still cap members at 5.
  const sourceTeams = Array.isArray(teams) ? teams : DEFAULT_TEAMS.slice(0, numTeams);

  const resolvedTeams = sourceTeams.map((t, i) => ({
    id: t.id ?? i + 1,
    name: t.name ?? TEAM_PLACEHOLDER_NAME,
    members: Array.isArray(t.members)
      ? t.members.slice(0, 5)
      : Array.from({ length: Math.min(5, membersPerTeam) }, (_, m) => `Член ${m + 1}`),
  }));

  return (
    <main
      className="min-h-screen"
      style={{
        ...bodyFont,
        backgroundColor: COLORS.primary,
        color: COLORS.sand,
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 flex flex-col gap-10">
        {/* Header */}
        <header className="flex flex-col gap-6">
          <div className="space-y-3">
            <p
              className="uppercase tracking-[0.35em] text-base sm:text-lg"
              style={titleFont}
            >
              FUTURATHON &apos;25
            </p>
            <h1
              className="font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl"
              style={titleFont}
            >
              УЧЕСНИЧКИ ТИМОВИ
            </h1>
            {/* Bigger paragraph for the list description */}
            <p className="max-w-2xl text-xl sm:text-2xl leading-relaxed">
              Листа на сите тимови кои учествуваат на{" "}
              <span className="font-semibold">Futurathon &apos;25</span>.
            </p>
          </div>

          {/* Info bar */}
          <section
            className="rounded-3xl px-6 py-4 sm:px-8 sm:py-5 flex flex-wrap items-center gap-4 text-lg sm:text-xl"
            style={{
              backgroundColor: COLORS.accent,
              color: COLORS.dark,
            }}
          >
            <span
              className="inline-flex items-center gap-2 font-semibold"
              style={{ fontSize: "1.35rem" }}
            >
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: COLORS.green }}
              />
              Вкупно тимови:
              <span style={titleFont}> {resolvedTeams.length}</span>
            </span>

            <span className="hidden sm:inline-block h-6 w-px opacity-40 bg-black" />

            <span className="opacity-90 leading-relaxed">
              Доколку има промена во името или составот на тимот, контактирај го{" "}
              <span className="font-semibold">
                одговорниот за натпреварувачи (PAX Responsible).
              </span>
            </span>
          </section>
        </header>

        {/* Teams section */}
        <section className="space-y-5">
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-wide"
            style={titleFont}
          >
            Тимови
          </h2>

          <div className="grid gap-6 sm:gap-7 md:grid-cols-2 lg:grid-cols-3 items-stretch">
            {resolvedTeams.map((team) => (
              <article
                key={team.id}
                className="rounded-3xl p-6 sm:p-7 shadow-xl flex flex-col gap-4 h-full min-h-[260px]"
                style={{
                  backgroundColor: COLORS.sand,
                  color: COLORS.dark,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.25)",
                }}
              >
                  {/* Team header */}
                  <div className="flex items-start justify-between gap-3">
                    <h3
                      className="leading-snug text-2xl sm:text-3xl break-words"
                      style={titleFont}
                    >
                      {TEAM_LABEL}
                    </h3>
                    <span
                      className="uppercase tracking-wide px-3 py-1 rounded-full whitespace-nowrap text-sm sm:text-base"
                      style={{
                        ...titleFont,
                        backgroundColor: COLORS.green, // green pill
                        color: "#FFFFFF", // pill text is now white
                      }}
                    >
                      {team.name || TEAM_PLACEHOLDER_NAME}
                    </span>
                  </div>

                  {/* Members – member lines now bigger */}
                  <div className="pt-1 flex-1">
                    <p
                      className="font-semibold mb-3 text-lg sm:text-xl"
                      style={titleFont}
                    >
                      Членови:
                    </p>

                    <div className="grid grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-3">
                      {(team.members || []).slice(0, 5).map((member, i) => {
                        const { first, last } = splitName(member);
                        return (
                          <div
                            key={`${team.id}-${i}`}
                            className="flex items-start gap-2"
                          >
                            <span style={{ fontSize: '1.75rem' }}>•</span>
                            <span style={{ fontSize: '1.75rem' }}>
                              <span className="block text-3xl">{first}</span>
                              {last && <span className="block text-3xl">{last}</span>}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </article>
            ))}
          </div>
        </section>

        {/* Footer note */}
        <footer className="pt-4 text-sm sm:text-base opacity-90 leading-relaxed">
          <p>
            * Овој список е креиран според последните пријави за Futurathon
            &apos;25 и ќе биде ажуриран доколку има промени од страна на
            Одговорниот за партиципанти.
          </p>
        </footer>
      </div>
    </main>
  );
}

export default MainContent;
